import React, { useState } from 'react';
import { ChevronDownIcon, CheckIcon, ClockIcon, UsersIcon, ShieldCheckIcon, GlobeIcon } from 'lucide-react';
import { CRESTMONT } from './CrestmontChrome';
import { CRESTMONT_ACCREDITORS, type BenefitCard, type CrestmontProgram, type FaqEntry } from '../../data/crestmontPrograms';
const BENEFIT_ICONS = {
  clock: ClockIcon,
  users: UsersIcon,
  shield: ShieldCheckIcon,
  globe: GlobeIcon
} as const;
export function CtaBand({
  heading,
  body,
  onApply
}: {
  heading: string;
  body: string;
  onApply: () => void;
}) {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CRESTMONT.navy
  }} data-id="element-339" data-mp="u1ozybr9i1gv">
      {/* Gold wedge from the brand guide — decorative, hidden on mobile */}
      <div className="hidden md:block absolute inset-y-0 right-0 w-1/4" style={{
      backgroundColor: CRESTMONT.gold,
      clipPath: 'polygon(28% 0, 100% 0, 100% 100%, 0% 100%)'
    }} aria-hidden="true" data-id="element-340" data-mp="yyfdosflgkz4" />
      <div className="relative max-w-6xl mx-auto px-4 py-10 flex flex-col md:flex-row md:items-center gap-6" data-id="element-341" data-mp="2pvr3tgn8ezg8">
        <div className="md:max-w-2xl" data-id="element-342" data-mp="2kwxnzhkh2wcw">
          <h2 className="text-xl md:text-2xl font-extrabold text-white tracking-tight" data-id="element-343" data-mp="2kts3ygqpako1">
            {heading}
          </h2>
          <p className="mt-2 text-sm text-white/70 leading-relaxed" data-id="element-344" data-mp="2ut93egfd02bk">{body}</p>
        </div>
        <button onClick={onApply} className="md:ml-auto flex-shrink-0 px-6 py-3 rounded text-sm font-bold bg-white transition-colors hover:bg-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white" style={{
        color: CRESTMONT.navy
      }} data-id="element-345" data-mp="38kxgon1cgwu4">
          Apply Now
        </button>
      </div>
    </section>;
}
export function DifferentiatorsSection({
  program
}: {
  program: CrestmontProgram;
}) {
  const {
    differentiators
  } = program;
  const [activeId, setActiveId] = useState(differentiators.tabs[0].id);
  const active = differentiators.tabs.find(t => t.id === activeId) || differentiators.tabs[0];
  return <section className="py-16 md:py-20" style={{
    backgroundColor: CRESTMONT.paper
  }} aria-labelledby="unique-heading" data-id="element-346" data-mp="2cvqg27o9uer1">
      <div className="max-w-5xl mx-auto px-4" data-id="element-347" data-mp="2hti4grppjiqh">
        <h2 id="unique-heading" className="text-center text-3xl md:text-4xl font-extrabold tracking-tight" style={{
        color: CRESTMONT.navy
      }} data-id="element-348" data-mp="2mtwe2e6yj0qm">
          {differentiators.heading}
        </h2>
        <p className="mt-3 text-center text-sm text-gray-600 max-w-2xl mx-auto" data-id="element-349" data-mp="2vtsvpvuxizc6">
          {differentiators.subheading}
        </p>

        <div className="mt-10 grid gap-6 md:grid-cols-2 md:items-start" data-id="element-350" data-mp="30mci9iblh4t4">
          <figure className="relative rounded-lg overflow-hidden" data-id="element-351" data-mp="35jcfr4udoz8m">
            <img src={differentiators.imageUrl} alt={`${program.shortName} graduate`} className="w-full h-64 md:h-80 object-cover object-top" data-id="element-352" data-mp="3aka8gbd0lbod" />
            <figcaption className="absolute inset-x-0 bottom-0 p-5" style={{
            backgroundColor: 'rgba(11, 35, 56, 0.82)'
          }} data-id="element-353" data-mp="13w15vtr4xrcz">
              <p className="text-sm font-semibold text-white leading-snug" data-id="element-354" data-mp="tx250xcm300j">
                “{differentiators.imageQuote}”
              </p>
              <p className="mt-1.5 text-[11px] font-bold uppercase tracking-wide text-[#fcc23f]" data-id="element-355" data-mp="oziqdwc04l82">
                {differentiators.imageAttribution}
              </p>
            </figcaption>
          </figure>

          <div data-id="element-356" data-mp="1hnofeva38wdp">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Program differentiators" data-id="element-357" data-mp="1hozfol4brm02">
              {differentiators.tabs.map(tab => {
              const isActive = tab.id === active.id;
              return <button key={tab.id} role="tab" id={`diff-tab-${tab.id}`} aria-selected={isActive} aria-controls={`diff-panel-${tab.id}`} onClick={() => setActiveId(tab.id)} className="px-3.5 py-2 rounded text-[11px] font-bold border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#00426b]" style={isActive ? {
                backgroundColor: CRESTMONT.navy,
                borderColor: CRESTMONT.navy,
                color: '#ffffff'
              } : {
                backgroundColor: '#ffffff',
                borderColor: '#d4d9df',
                color: CRESTMONT.navy
              }} data-id="element-358" data-mp="25ek25dzot1e3">
                    {tab.label}
                  </button>;
            })}
            </div>

            <div id={`diff-panel-${active.id}`} role="tabpanel" aria-labelledby={`diff-tab-${active.id}`} className="mt-4 bg-white border border-gray-200 rounded-lg p-6" data-id="element-359" data-mp="g4awmypk40px">
              <h3 className="text-lg font-bold" style={{
              color: CRESTMONT.navy
            }} data-id="element-360" data-mp="pu48m0tm2hup">
                {active.heading}
              </h3>
              <ul className="mt-4 space-y-2.5" data-id="element-361" data-mp="3tcfb4av2inww">
                {active.bullets.map(bullet => <li key={bullet} className="flex items-start gap-2.5" data-id="element-362" data-mp="3odb4nrzj34a9">
                    <CheckIcon className="mt-0.5 w-4 h-4 flex-shrink-0" style={{
                  color: CRESTMONT.navy
                }} aria-hidden="true" data-id="element-363" data-mp="6768itc5hv6h" />
                    <span className="text-sm leading-relaxed text-gray-700" data-id="element-364" data-mp="1hl1y6w0olmby">
                      {bullet}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
export function RequirementsSection({
  program,
  onApply
}: {
  program: CrestmontProgram;
  onApply: () => void;
}) {
  const {
    requirements
  } = program;
  const [activeId, setActiveId] = useState(requirements.tabs[0].id);
  const active = requirements.tabs.find(t => t.id === activeId) || requirements.tabs[0];
  return <section className="py-16 md:py-20 bg-white" aria-labelledby="requirements-heading" data-id="element-365" data-mp="3np08znmtvq5o">
      <div className="max-w-5xl mx-auto px-4" data-id="element-366" data-mp="3sod6d2bbsfmi">
        <h2 id="requirements-heading" className="text-center text-2xl md:text-3xl font-extrabold tracking-tight" style={{
        color: CRESTMONT.navy
      }} data-id="element-367" data-mp="3skxnrpig0ogc">
          {program.name} Requirements
        </h2>

        <div className="mt-10 grid gap-6 lg:grid-cols-5 lg:items-start" data-id="element-368" data-mp="34yiv2p6nmz4d">
          <div className="lg:col-span-3 rounded-lg p-5 md:p-7" style={{
          backgroundColor: CRESTMONT.paper
        }} data-id="element-369" data-mp="38zb35f3sixz2">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Requirements" data-id="element-370" data-mp="31eienyld6u9t">
              {requirements.tabs.map(tab => {
              const isActive = tab.id === active.id;
              return <button key={tab.id} role="tab" id={`req-tab-${tab.id}`} aria-selected={isActive} aria-controls={`req-panel-${tab.id}`} onClick={() => setActiveId(tab.id)} className="px-3.5 py-2 rounded text-[11px] font-bold border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#00426b]" style={isActive ? {
                backgroundColor: CRESTMONT.navy,
                borderColor: CRESTMONT.navy,
                color: '#ffffff'
              } : {
                backgroundColor: '#ffffff',
                borderColor: '#d4d9df',
                color: CRESTMONT.navy
              }} data-id="element-371" data-mp="2ngtsmnfk9h36">
                    {tab.label}
                  </button>;
            })}
            </div>
            <ul id={`req-panel-${active.id}`} role="tabpanel" aria-labelledby={`req-tab-${active.id}`} className="mt-5 space-y-3" data-id="element-372" data-mp="2zw05yflfjdhs">
              {active.items.map(item => <li key={item} className="flex items-start gap-2.5" data-id="element-373" data-mp="2uy723t7lmvko">
                  <CheckIcon className="mt-0.5 w-4 h-4 flex-shrink-0" style={{
                color: CRESTMONT.navy
              }} aria-hidden="true" data-id="element-374" data-mp="2uwdxskkgknfu" />
                  <span className="text-sm leading-relaxed text-gray-700" data-id="element-375" data-mp="2lzmrlfc6zs7j">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>

          <div className="lg:col-span-2 border border-gray-200 rounded-lg p-6" data-id="element-376" data-mp="1xf09b5iuoafx">
            <h3 className="text-base font-bold" style={{
            color: CRESTMONT.navy
          }} data-id="element-377" data-mp="2cbr574tbf40s">
              {program.shortName} Online Tuition
            </h3>
            <dl className="mt-5 space-y-3 text-sm" data-id="element-378" data-mp="2l99xmu9agkmp">
              <div className="flex items-baseline justify-between gap-4" data-id="element-379" data-mp="2gc1byjoxlygp">
                <dt className="text-gray-600" data-id="element-380" data-mp="2g9xbycu5ljlp">Tuition per credit</dt>
                <dd className="font-bold tabular-nums text-gray-900" data-id="element-381" data-mp="2v04ko0uczo97">
                  {requirements.tuition.perCredit}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4" data-id="element-382" data-mp="340ucktgi0uhn">
                <dt className="text-gray-600" data-id="element-383" data-mp="33yztzlsp9egq">Credits required</dt>
                <dd className="font-bold tabular-nums text-gray-900" data-id="element-384" data-mp="301btm388mthg">
                  {requirements.tuition.credits}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4 pb-3 border-b border-gray-200" data-id="element-385" data-mp="xm5xop8q36ie">
                <dt className="text-gray-600" data-id="element-386" data-mp="smtdkc98kdji">
                  {requirements.tuition.feesLabel}
                </dt>
                <dd className="font-bold tabular-nums text-gray-900" data-id="element-387" data-mp="npt5jvstt0pr">
                  {requirements.tuition.feesValue}
                </dd>
              </div>
            </dl>
            <div className="mt-4 rounded px-4 py-3 flex items-baseline justify-between gap-4" style={{
            backgroundColor: CRESTMONT.navy
          }} data-id="element-388" data-mp="1gcw24idxz1dd">
              <span className="text-sm font-bold text-white" data-id="element-389" data-mp="17cr5n3aadndz">
                Total program tuition
              </span>
              <span className="text-base font-extrabold tabular-nums text-[#fcc23f]" data-id="element-390" data-mp="3w4jvy4xnsdry">
                {requirements.tuition.total}
              </span>
            </div>
            <button onClick={onApply} className="mt-4 w-full py-3 rounded text-sm font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#00426b]" style={{
            backgroundColor: CRESTMONT.gold,
            color: CRESTMONT.ink
          }} data-id="element-391" data-mp="27knw0fwidpas">
              Request Information
            </button>
            <p className="mt-3 text-[10px] leading-relaxed text-gray-500" data-id="element-392" data-mp="2ldx0c25lnkmg">
              Estimate based on the standard plan at the current per-credit rate.
              Transfer credit lowers the total. Financial aid, military rates,
              and employer benefits are applied separately.
            </p>
          </div>
        </div>
      </div>
    </section>;
}
export function BenefitsSection({
  benefits
}: {
  benefits: BenefitCard[];
}) {
  return <section className="py-16 md:py-20" style={{
    backgroundColor: CRESTMONT.paper
  }} aria-labelledby="benefits-heading" data-id="element-393" data-mp="14z44ymd8wrwd">
      <div className="max-w-5xl mx-auto px-4" data-id="element-394" data-mp="19yqfup5lpw20">
        <h2 id="benefits-heading" className="text-center text-2xl md:text-3xl font-extrabold tracking-tight" style={{
        color: CRESTMONT.navy
      }} data-id="element-395" data-mp="2d8ui1gozgwcg">
          Advance your career without pausing your life.
        </h2>
        <p className="mt-3 text-center text-sm text-gray-600" data-id="element-396" data-mp="2m64yrhkkldor">
          The four things Crestmont students say made the difference.
        </p>
        <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-397" data-mp="2w5nefltg6db0">
          {benefits.map(benefit => {
          const Icon = BENEFIT_ICONS[benefit.icon];
          return <li key={benefit.title} className="bg-white border border-gray-200 rounded-lg p-6 flex flex-col" data-id="element-398" data-mp="34z8st41p9sjw">
                <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{
              backgroundColor: CRESTMONT.navy
            }} data-id="element-399" data-mp="30zrncam9wveq">
                  <Icon className="w-5 h-5 text-white" aria-hidden="true" data-id="element-400" data-mp="39vz9ntlbws6d" />
                </span>
                <p className="mt-4 text-[11px] font-bold uppercase tracking-wide text-gray-500" data-id="element-401" data-mp="3thsmmy9jfp8w">
                  {benefit.eyebrow}
                </p>
                <p className="mt-auto pt-1.5 text-sm font-bold leading-snug" style={{
              color: CRESTMONT.navy
            }} data-id="element-402" data-mp="3jko02xbs5rrk">
                  {benefit.title}
                </p>
              </li>;
        })}
        </ul>

        <div className="mt-12 pt-10 border-t border-gray-200" data-id="element-403" data-mp="3fjue49hwen1b">
          <p className="text-center text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500" data-id="element-404" data-mp="3fm73v8twe1ox">
            Accreditation & authorization
          </p>
          <ul className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6" data-id="element-405" data-mp="35thrmbqiwjsj">
            {CRESTMONT_ACCREDITORS.map(accreditor => <li key={accreditor.abbr} className="text-center" data-id="element-406" data-mp="2rwmikja9usch">
                <span className="block text-lg font-extrabold tracking-tight" style={{
              color: CRESTMONT.navy
            }} data-id="element-407" data-mp="2vutp6lb0ud9y">
                  {accreditor.abbr}
                </span>
                <span className="mt-1 block text-[10px] leading-snug text-gray-500" data-id="element-408" data-mp="2julum9wicsph">
                  {accreditor.name}
                </span>
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}
function FaqItem({
  entry,
  index
}: {
  entry: FaqEntry;
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200" data-id="element-409" data-mp="212ayr009tv0v">
      <button type="button" onClick={() => setIsOpen(prev => !prev)} aria-expanded={isOpen} aria-controls={`faq-answer-${index}`} className="w-full py-4 flex items-start justify-between gap-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00426b] rounded" data-id="element-410" data-mp="215p3geweqgbh">
        <span className="text-sm font-bold" style={{
        color: CRESTMONT.navy
      }} data-id="element-411" data-mp="3lio2uuy7tais">
          {entry.question}
        </span>
        <ChevronDownIcon className={`mt-0.5 w-4 h-4 flex-shrink-0 text-gray-400 transition-transform duration-200 ease-out ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-412" data-mp="3tzy4sq3rx6vw" />
      </button>
      {isOpen && <p id={`faq-answer-${index}`} className="pb-4 pr-8 text-sm leading-relaxed text-gray-600" data-id="element-413" data-mp="bmguponkoty9">
          {entry.answer}
        </p>}
    </div>;
}
export function FaqSection({
  faq
}: {
  faq: FaqEntry[];
}) {
  return <section className="py-16 md:py-20 bg-white" aria-labelledby="faq-heading" data-id="element-414" data-mp="3f4qrwp6cn2gi">
      <div className="max-w-3xl mx-auto px-4" id="faq" data-id="element-415" data-mp="3k43gpfgs6jlq">
        <h2 id="faq-heading" className="text-center text-2xl md:text-3xl font-extrabold tracking-tight" style={{
        color: CRESTMONT.navy
      }} data-id="element-416" data-mp="3p1dblamxvngy">
          Frequently Asked Questions
        </h2>
        <div className="mt-8" data-id="element-417" data-mp="149b9q61mf14c">
          {faq.map((entry, index) => <FaqItem key={entry.question} entry={entry} index={index} data-id="element-418" data-mp="elpciqd1ob54" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}